import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, Upload, X, Sparkles, ShieldCheck, Heart, Award } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILES = 5;
const MAX_FILE_MB = 4;

const aboutItems = [
  { icon: Award, title: "Premium Υλικά", desc: "Χρησιμοποιούμε flex neon LED υψηλής ποιότητας με χαμηλή κατανάλωση και διάρκεια ζωής 50.000+ ωρών." },
  { icon: Sparkles, title: "Μεράκι σε κάθε project", desc: "Κάθε επιγραφή φτιάχνεται στο χέρι, με προσοχή στη λεπτομέρεια και πάθος για το αποτέλεσμα." },
  { icon: ShieldCheck, title: "Πλήρης επαγγελματισμός", desc: "Χρόνια εμπειρίας στον χώρο μας επιτρέπουν να σου εγγυηθούμε ποιότητα και έγκαιρη παράδοση." },
  { icon: Heart, title: "Δεμένοι με την ιδέα σου", desc: "Δουλεύουμε μαζί σου από το πρώτο σχέδιο μέχρι την παράδοση — η οπτική σου είναι η αποστολή μας." },
];

export const MyProjectPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phrase: "", description: "" });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    const all = [...files, ...incoming];
    if (all.length > MAX_FILES) {
      toast.error(`Έως ${MAX_FILES} αρχεία επιτρέπονται.`);
      return;
    }
    for (const f of incoming) {
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`Το αρχείο "${f.name}" υπερβαίνει τα ${MAX_FILE_MB}MB.`);
        return;
      }
    }
    setFiles(all);
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.description) {
      toast.error("Συμπληρώστε όνομα, email και περιγραφή.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phrase", form.phrase || "");
      fd.append("description", form.description);
      files.forEach((f) => fd.append("files", f));

      await axios.post(`${API}/projects`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Το αίτημά σου εστάλη! Θα επικοινωνήσουμε μαζί σου σύντομα.");
      setForm({ name: "", email: "", phrase: "", description: "" });
      setFiles([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Αποτυχία αποστολής. Δοκιμάστε ξανά.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen" data-testid="my-project-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 neon-grid-bg opacity-30 pointer-events-none" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(176,38,255,0.18), transparent 60%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <div className="text-xs tracking-[0.3em] uppercase text-[#B026FF] font-bold mb-4">
            Custom Neon Project
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
            Η <span className="text-[#B026FF] neon-glow-pink">ιδέα</span> σου
          </h1>
          <p className="text-zinc-400 mt-6 max-w-2xl mx-auto leading-relaxed">
            Έχεις μια ιδέα; Στείλε μας τη φράση, το χρώμα και τις διαστάσεις που έχεις στο
            μυαλό σου — ή ανέβασε σχέδια &amp; φωτογραφίες αναφοράς. Σε λίγες ημέρες θα
            έχεις το δικό σου μοναδικό neon στα χέρια σου.
          </p>
        </div>
      </section>

      {/* About */}
      <section className="py-16 md:py-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">
            Γιατί GKLed
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight max-w-3xl">
            Γνωρίζουμε τον χώρο μας — και βάζουμε <span className="text-[#00FFFF] neon-glow-cyan">μεράκι</span> σε κάθε project.
          </h2>
          <p className="text-zinc-400 mt-6 max-w-3xl leading-relaxed">
            Ως ομάδα εξειδικευμένη στο flex neon, δουλεύουμε μόνο με premium υλικά και
            εφαρμόζουμε τεχνικές που έχουμε τελειοποιήσει στη διάρκεια χρόνων. Δεν είμαστε
            απλοί κατασκευαστές — είμαστε δημιουργοί που πιστεύουμε στην ομορφιά κάθε
            ιδέας. Από τη μικρότερη φράση μέχρι το πιο σύνθετο επαγγελματικό λογότυπο,
            σε κάθε project βάζουμε επαγγελματισμό, προσοχή στη λεπτομέρεια και αληθινή
            αγάπη για αυτό που κάνουμε.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {aboutItems.map((it, i) => (
              <motion.div
                key={it.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="p-6 rounded-2xl border border-white/5 bg-[#050505] hover:border-[#B026FF]/30 transition-colors"
                data-testid={`about-item-${i}`}
              >
                <it.icon size={22} className="text-[#B026FF] mb-4" strokeWidth={1.5} />
                <div className="font-heading font-semibold text-lg mb-2">{it.title}</div>
                <div className="text-sm text-zinc-400 leading-relaxed">{it.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">
            Πες μας την ιδέα σου
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight mb-8">
            Στείλε το αίτημά σου
          </h2>

          <form
            onSubmit={handleSubmit}
            className="bg-[#050505] border border-white/5 rounded-2xl p-6 md:p-8 flex flex-col gap-5"
            data-testid="project-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Όνομα" required>
                <input
                  value={form.name}
                  onChange={update("name")}
                  className="neon-input"
                  required
                  data-testid="project-input-name"
                />
              </Field>
              <Field label="Email" required>
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="neon-input"
                  required
                  data-testid="project-input-email"
                />
              </Field>
            </div>

            <Field label="Κείμενο / Φράση (προαιρετικό)">
              <input
                value={form.phrase}
                onChange={update("phrase")}
                placeholder="π.χ. Just Married, Open, Καφέ Όνομα..."
                className="neon-input"
                data-testid="project-input-phrase"
              />
            </Field>

            <Field label="Αναλυτική περιγραφή του project" required>
              <textarea
                rows={6}
                value={form.description}
                onChange={update("description")}
                placeholder="Πες μας τι έχεις στο μυαλό σου: χρώμα, διαστάσεις, που θα τοποθετηθεί, στυλ γραμματοσειράς..."
                className="neon-input resize-none"
                required
                data-testid="project-input-description"
              />
            </Field>

            {/* File upload */}
            <Field label="Φωτογραφίες / Αρχεία αναφοράς (προαιρετικό)">
              <label
                className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-white/10 rounded-xl p-8 cursor-pointer hover:border-[#B026FF]/50 hover:bg-white/[0.02] transition-all"
                data-testid="project-file-dropzone"
              >
                <Upload size={28} className="text-[#B026FF]" />
                <div className="text-center">
                  <div className="text-sm font-medium">Κάνε κλικ ή σύρε αρχεία εδώ</div>
                  <div className="text-xs text-zinc-500 mt-1">Έως {MAX_FILES} αρχεία · max {MAX_FILE_MB}MB το καθένα · εικόνες ή PDF</div>
                </div>
                <input
                  type="file"
                  multiple
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={handleFiles}
                  data-testid="project-file-input"
                />
              </label>

              {files.length > 0 && (
                <div className="flex flex-col gap-2 mt-3">
                  {files.map((f, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-white/5 bg-[#0A0A0A] text-sm"
                      data-testid={`project-file-item-${i}`}
                    >
                      <span className="truncate flex-1">{f.name}</span>
                      <span className="text-xs text-zinc-500">{(f.size / 1024 / 1024).toFixed(2)}MB</span>
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        className="text-zinc-500 hover:text-[#B026FF]"
                        data-testid={`project-file-remove-${i}`}
                        aria-label="Αφαίρεση"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Field>

            <button
              type="submit"
              disabled={submitting}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#B026FF] text-black px-6 py-4 rounded-lg font-bold hover:neon-box-glow-pink transition-all disabled:opacity-40"
              data-testid="project-submit"
            >
              {submitting ? "Αποστολή..." : (<><Send size={16} /> Στείλε το αίτημα</>)}
            </button>
          </form>
        </div>
      </section>

      <style>{`
        .neon-input {
          width: 100%;
          background: #0A0A0A;
          border: 1px solid rgba(255,255,255,0.08);
          color: white;
          padding: 0.75rem 1rem;
          border-radius: 0.5rem;
          font-size: 0.9rem;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .neon-input:focus {
          border-color: #B026FF;
          box-shadow: 0 0 0 1px #B026FF;
        }
      `}</style>
    </main>
  );
};

const Field = ({ label, required, children }) => (
  <label className="flex flex-col gap-2">
    <span className="text-xs text-zinc-400 font-medium">
      {label} {required && <span className="text-[#B026FF]">*</span>}
    </span>
    {children}
  </label>
);

export default MyProjectPage;
